import { cn, tv, VariantProps } from "@utils/tw"
import { ComponentProps, forwardRef } from "react"
import { Icon, IconRef } from "./Icon"
import { Spinner } from "./Spinner"

type InputProps = Omit<ComponentProps<"input">, "size"> &
  VariantProps<typeof inputStyles> & {
    before?: IconRef
    after?: IconRef
    onValueChange?: (value: string) => void
    innerClassName?: string
  }

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { className, innerClassName, type, before, after, loading, stretch, variant, onValueChange, size, ...props },
    ref,
  ) => {
    const styles = inputStyles({
      variant,
      size,
      loading,
      stretch,
    })
    return (
      <div className={cn(styles.base(), className)}>
        {before ? <Icon data-part="icon" name={before} /> : null}
        <input
          type={type}
          className={cn(styles.input(), innerClassName)}
          ref={ref}
          {...props}
          onChange={
            onValueChange
              ? (e) => {
                  onValueChange!(e.target.value)
                  props.onChange?.(e)
                }
              : props.onChange
          }
        />
        {loading ? <Spinner data-part="spinner" /> : null}
        {after ? <Icon data-part="icon" name={after} /> : null}
      </div>
    )
  },
)
Input.displayName = "Input"

export const inputStyles = tv({
  base: [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap relative",
    "type-label-m transition-colors",
    "focus-within:ring-input disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    "first:part-[icon]:-ml-1",
    "last:part-[icon]:-mr-1",
    "[&_.icon]:size-[1.4em] part-[spinner]:size-[1.3em]",
    "part-[icon]:flex-none",
    "part-[spinner]:absolute part-[spinner]:top-1/2 part-[spinner]:left-1/2 part-[spinner]:transform part-[spinner]:-translate-x-1/2 part-[spinner]:-translate-y-1/2",
  ],
  slots: {
    input:
      "h-full flex-1 outline-none border-0 bg-transparent placeholder:opacity-50 placeholder:text-black placeholder:uppercase",
  },
  variants: {
    variant: {
      default: "bg-secondary text-secondary-foreground border border-accent",
      destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      outline: "border-[2px] border-primary bg-background hover:bg-accent hover:text-accent-foreground",
    },
    size: {
      default: "h-control px-3",
      sm: "h-control-sm px-3",
      lg: "h-control-lg px-4",
      icon: "h-control w-control",
    },
    loading: {
      true: ["pointer-events-none opacity-90", "[&_>_*]:opacity-0 part-[spinner]:opacity-100"],
    },
    stretch: {
      true: "flex-1",
    },
  },
  defaultVariants: {
    size: "default",
    variant: "default",
  },
})
